import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Heart, Volume2, VolumeX, Play, ChevronUp, ChevronDown } from 'lucide-react';
import {
  getDramasByTest,
  detectLanguage,
  selectRandomDrama,
  defaultTestId,
  testExists,
  getAllTestIds
} from './dramaConfig';
import { useTranslation } from '../../../i18n/index';
import './ShowTest.css';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function ShowTest() {
  const query = useQuery();
  const [drama, setDrama] = useState(null);
  const [language, setLanguage] = useState('en');
  const [testId, setTestId] = useState(defaultTestId);
  const [isMuted, setIsMuted] = useState(true);
  const [likedEpisodes, setLikedEpisodes] = useState({}); // Track liked status for each episode
  const [episodeLikeCounts, setEpisodeLikeCounts] = useState({}); // Track like counts for each episode
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const videoRefs = useRef({});
  const containerRef = useRef(null);
  const initializedRef = useRef(false);
  const playbackTrackedRef = useRef({}); // Track which playback events have been sent for each episode

  useEffect(() => {
    // Only initialize once to prevent infinite loop
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Get language from URL query parameter or detect from browser
    // Support 'lang' and 'language' as parameter names
    const urlLang = query.get('lang') || query.get('language');
    let detectedLang;
    if (urlLang && ['en', 'es', 'pt'].includes(urlLang.toLowerCase())) {
      detectedLang = urlLang.toLowerCase();
    } else {
      detectedLang = detectLanguage();
    }
    setLanguage(detectedLang);

    // Get testId from URL query parameter (if provided)
    // Support both 'testId' and 'test' as parameter names
    const urlTestId = query.get('testId') || query.get('test');

    let selectedDrama = null;
    let currentTestId = null; // Track the test ID being used

    if (urlTestId && testExists(urlTestId)) {
      // URL parameter provided: use it and randomly select a drama from that test
      // But still check for stored drama first (URL can override)
      const storedDramaId = localStorage.getItem('userDramaId');

      if (storedDramaId) {
        // Try to find the stored drama in the specified test group
        const dramas = getDramasByTest(urlTestId, detectedLang);
        const foundDrama = dramas.find(d => d.id === storedDramaId);

        if (foundDrama) {
          // Use stored drama even when URL testId is provided
          selectedDrama = foundDrama;
          currentTestId = urlTestId;
          setTestId(urlTestId);
          console.log('📦 Using stored drama with URL testId:', storedDramaId);
        } else {
          // Stored drama not in this test group, randomly select from URL test group
          selectedDrama = selectRandomDrama(dramas);
          currentTestId = urlTestId;
          setTestId(urlTestId);
          // Save the new drama assignment
          if (selectedDrama) {
            localStorage.setItem('userDramaId', selectedDrama.id);
            console.log('💾 Saved new drama (URL testId override):', selectedDrama.id);
          }
        }
      } else {
        // No stored drama, randomly select from URL test group
        const dramas = getDramasByTest(urlTestId, detectedLang);
        selectedDrama = selectRandomDrama(dramas);
        currentTestId = urlTestId;
        setTestId(urlTestId);
        // Save the new drama assignment
        if (selectedDrama) {
          localStorage.setItem('userDramaId', selectedDrama.id);
          console.log('💾 Saved new drama (URL testId, first visit):', selectedDrama.id);
        }
      }
    } else {
      // Check if user has a stored drama assignment
      const storedDramaId = localStorage.getItem('userDramaId');

      if (storedDramaId) {
        // Try to find the stored drama in all test groups
        const allTestIds = getAllTestIds();
        let foundDrama = null;
        let foundTestId = null;

        for (const testId of allTestIds) {
          const dramas = getDramasByTest(testId, detectedLang);
          foundDrama = dramas.find(d => d.id === storedDramaId);
          if (foundDrama) {
            foundTestId = testId;
            break;
          }
        }

        if (foundDrama) {
          // Use stored drama
          selectedDrama = foundDrama;
          currentTestId = foundTestId;
          setTestId(foundTestId);
          console.log('📦 Using stored drama:', storedDramaId);
        } else {
          // Stored drama not found (maybe language changed), assign new one
          const allTestIds = getAllTestIds();
          if (allTestIds.length > 0) {
            const randomTestId = allTestIds[Math.floor(Math.random() * allTestIds.length)];
            const dramas = getDramasByTest(randomTestId, detectedLang);
            selectedDrama = selectRandomDrama(dramas);
            currentTestId = randomTestId;
            setTestId(randomTestId);

            // Store the new assignment
            if (selectedDrama) {
              localStorage.setItem('userDramaId', selectedDrama.id);
              console.log('🎲 New drama assigned (stored not found):', selectedDrama.id);
            }
          }
        }
      } else {
        // First visit: randomly assign a drama from all available test groups
        const allTestIds = getAllTestIds();
        console.log('🔍 First visit - Available test IDs:', allTestIds);

        if (allTestIds.length > 0) {
          // Randomly select a test group
          const randomTestId = allTestIds[Math.floor(Math.random() * allTestIds.length)];
          const dramas = getDramasByTest(randomTestId, detectedLang);
          console.log('📚 Test group dramas:', randomTestId, dramas);
          selectedDrama = selectRandomDrama(dramas);
          currentTestId = randomTestId;
          setTestId(randomTestId);

          // Store the drama ID in localStorage
          if (selectedDrama) {
            localStorage.setItem('userDramaId', selectedDrama.id);
            console.log('💾 Saved to localStorage - drama ID:', selectedDrama.id);
            console.log('🎲 New user assigned to drama:', selectedDrama.id);
          } else {
            console.error('❌ selectedDrama is null after selectRandomDrama');
          }
        } else {
          // Fallback to default
          console.log('⚠️ No test IDs found, using default:', defaultTestId);
          const dramas = getDramasByTest(defaultTestId, detectedLang);
          selectedDrama = selectRandomDrama(dramas);
          currentTestId = defaultTestId;
          setTestId(defaultTestId);

          // Store the drama ID in localStorage even for default
          if (selectedDrama) {
            localStorage.setItem('userDramaId', selectedDrama.id);
            console.log('💾 Saved to localStorage (default) - drama ID:', selectedDrama.id);
          }
        }
      }
    }

    if (!selectedDrama) {
      console.error('❌ No drama found for language:', detectedLang);
      return;
    }

    // Always save the drama ID to localStorage (unless it's already the stored one)
    if (selectedDrama && selectedDrama.id) {
      try {
        const currentStored = localStorage.getItem('userDramaId');
        if (currentStored !== selectedDrama.id) {
          localStorage.setItem('userDramaId', selectedDrama.id);
          console.log('💾 Final save to localStorage - drama ID:', selectedDrama.id);
        }
        console.log('✅ localStorage check:', localStorage.getItem('userDramaId'));
      } catch (error) {
        console.error('❌ Failed to save to localStorage:', error);
      }
    }

    setDrama(selectedDrama);
    setCurrentEpisodeIndex(0);

    // Log for analytics (A/B testing)
    console.log('🎬 A/B Test - Selected Drama:', {
      test_id: currentTestId,
      drama_id: selectedDrama.id,
      language: detectedLang,
      title: selectedDrama.title,
      episodes_count: selectedDrama.episodes?.length || 1,
      timestamp: new Date().toISOString()
    });

    // Initialize like counts for each episode (random between 15000-20000)
    const episodes = selectedDrama.episodes || (selectedDrama.videoUrl ? [{ videoUrl: selectedDrama.videoUrl, title: selectedDrama.title, episodeNumber: 1 }] : []);
    const initialLikeCounts = {};
    const initialLikedStatus = {};

    episodes.forEach((_, index) => {
      // Generate random like count between 15000 and 20000
      const randomCount = Math.floor(Math.random() * (20000 - 15000 + 1)) + 15000;
      initialLikeCounts[index] = randomCount;
      initialLikedStatus[index] = false;
    });

    setEpisodeLikeCounts(initialLikeCounts);
    setLikedEpisodes(initialLikedStatus);
  }, [query]);

  // Get episodes array
  const episodes = drama?.episodes || (drama ? [{ videoUrl: drama.videoUrl, title: drama.title, episodeNumber: 1 }] : []);
  const totalEpisodes = episodes.length;

  // Get video ref for an episode index
  const getVideoRef = useCallback((index) => {
    if (!videoRefs.current[index]) {
      videoRefs.current[index] = React.createRef();
    }
    return videoRefs.current[index];
  }, []);

  // Play video at specific index
  const playVideo = useCallback(async (index) => {
    if (index < 0 || index >= totalEpisodes) return;

    const videoRef = getVideoRef(index);
    if (videoRef.current) {
      try {
        videoRef.current.currentTime = 0;
        await videoRef.current.play();
      } catch (error) {
        console.log('Autoplay prevented:', error);
      }
    }
  }, [totalEpisodes, getVideoRef]);

  // Pause video at specific index
  const pauseVideo = useCallback((index) => {
    if (index < 0 || index >= totalEpisodes) return;

    const videoRef = getVideoRef(index);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, [totalEpisodes, getVideoRef]);

  // Helper function to send GA events
  const sendGAEvent = useCallback((eventName, action, params = {}) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        action: action,
        test_group: drama?.id || 'unknown',
        episode_num: currentEpisodeIndex + 1,
        language: language,
        ...params
      });
      console.log(`📊 GA Event: ${eventName}`, { action, test_group: drama?.id, episode_num: currentEpisodeIndex + 1, language, ...params });
    }
  }, [drama, currentEpisodeIndex, language]);

  // Initialize and play current video
  useEffect(() => {
    if (drama && totalEpisodes > 0) {
      // Play current video
      playVideo(currentEpisodeIndex);

      // Preload next and previous videos
      if (currentEpisodeIndex + 1 < totalEpisodes) {
        const nextRef = getVideoRef(currentEpisodeIndex + 1);
        if (nextRef.current) {
          nextRef.current.load();
        }
      }
      if (currentEpisodeIndex - 1 >= 0) {
        const prevRef = getVideoRef(currentEpisodeIndex - 1);
        if (prevRef.current) {
          prevRef.current.load();
        }
      }
    }
  }, [drama, currentEpisodeIndex, totalEpisodes, playVideo, getVideoRef]);

  // Track video playback events (play_start, play_5s, play_30s, play_60s, play_complete)
  useEffect(() => {
    const currentRef = getVideoRef(currentEpisodeIndex);
    const video = currentRef.current;

    if (!video || !drama) return;

    // Reset tracking for this episode
    const episodeKey = `${drama.id}_${currentEpisodeIndex}`;
    playbackTrackedRef.current[episodeKey] = {
      play_start: false,
      play_5s: false,
      play_30s: false,
      play_60s: false,
      play_complete: false
    };

    const handlePlay = () => {
      if (!playbackTrackedRef.current[episodeKey].play_start) {
        sendGAEvent('playback', 'play_start');
        playbackTrackedRef.current[episodeKey].play_start = true;
      }
    };

    const handleTimeUpdate = () => {
      const currentTime = video.currentTime;
      const duration = video.duration;

      if (!playbackTrackedRef.current[episodeKey].play_5s && currentTime >= 5) {
        sendGAEvent('playback', 'play_5s');
        playbackTrackedRef.current[episodeKey].play_5s = true;
      }

      if (!playbackTrackedRef.current[episodeKey].play_30s && currentTime >= 30) {
        sendGAEvent('playback', 'play_30s');
        playbackTrackedRef.current[episodeKey].play_30s = true;
      }

      if (!playbackTrackedRef.current[episodeKey].play_60s && currentTime >= 60) {
        sendGAEvent('playback', 'play_60s');
        playbackTrackedRef.current[episodeKey].play_60s = true;
      }

      // Check if video is near completion (within 1 second of end)
      if (!playbackTrackedRef.current[episodeKey].play_complete && duration > 0 && currentTime >= duration - 1) {
        sendGAEvent('playback', 'play_complete');
        playbackTrackedRef.current[episodeKey].play_complete = true;
      }
    };

    const handleEnded = () => {
      if (!playbackTrackedRef.current[episodeKey].play_complete) {
        sendGAEvent('playback', 'play_complete');
        playbackTrackedRef.current[episodeKey].play_complete = true;
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, [currentEpisodeIndex, drama, getVideoRef, sendGAEvent]);

  // Handle episode switching
  const switchEpisode = useCallback((direction) => {
    if (isTransitioning || totalEpisodes <= 1) return;

    setIsTransitioning(true);

    if (direction === 'next' && currentEpisodeIndex < totalEpisodes - 1) {
      pauseVideo(currentEpisodeIndex);
      const nextIndex = currentEpisodeIndex + 1;
      setCurrentEpisodeIndex(nextIndex);
      setTimeout(() => {
        playVideo(nextIndex);
        setIsTransitioning(false);
      }, 300);
    } else if (direction === 'prev' && currentEpisodeIndex > 0) {
      pauseVideo(currentEpisodeIndex);
      const prevIndex = currentEpisodeIndex - 1;
      setCurrentEpisodeIndex(prevIndex);
      setTimeout(() => {
        playVideo(prevIndex);
        setIsTransitioning(false);
      }, 300);
    } else {
      setIsTransitioning(false);
    }
  }, [currentEpisodeIndex, totalEpisodes, isTransitioning, pauseVideo, playVideo]);

  // Listen for video end event to auto-play next episode
  useEffect(() => {
    const currentRef = getVideoRef(currentEpisodeIndex);
    const video = currentRef.current;

    if (!video || totalEpisodes <= 1) return;

    const handleVideoEnd = () => {
      // Auto switch to next episode if available
      if (currentEpisodeIndex < totalEpisodes - 1) {
        switchEpisode('next');
      }
    };

    video.addEventListener('ended', handleVideoEnd);

    return () => {
      video.removeEventListener('ended', handleVideoEnd);
    };
  }, [currentEpisodeIndex, totalEpisodes, getVideoRef, switchEpisode]);


  const toggleMute = () => {
    const currentRef = getVideoRef(currentEpisodeIndex);
    if (currentRef.current) {
      currentRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
      console.log('🔊 User action:', isMuted ? 'Unmuted' : 'Muted', {
        drama_id: drama?.id,
        episode_index: currentEpisodeIndex,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleLike = () => {
    const currentLiked = likedEpisodes[currentEpisodeIndex] || false;
    if (!currentLiked) {
      setLikedEpisodes(prev => ({
        ...prev,
        [currentEpisodeIndex]: true
      }));
      setEpisodeLikeCounts(prev => ({
        ...prev,
        [currentEpisodeIndex]: (prev[currentEpisodeIndex] || 0) + 1
      }));

      // Send GA event for like interaction
      sendGAEvent('interaction', 'like');

      // Send Facebook Pixel event
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', 'like_btn_click');
      }

      console.log('❤️ User action: Liked drama', {
        drama_id: drama?.id,
        episode_index: currentEpisodeIndex,
        timestamp: new Date().toISOString()
      });
    }
  };

  // Get current episode's like status and count
  const currentEpisodeLiked = likedEpisodes[currentEpisodeIndex] || false;
  const currentEpisodeLikeCount = episodeLikeCounts[currentEpisodeIndex] || 0;

  const handlePlayPause = () => {
    const currentRef = getVideoRef(currentEpisodeIndex);
    if (currentRef.current) {
      if (currentRef.current.paused) {
        currentRef.current.play();
      } else {
        currentRef.current.pause();
      }
    }
  };

  // Get translations
  const { t } = useTranslation(language);

  if (!drama) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100dvh', // Use dynamic viewport height for mobile
      minHeight: '-webkit-fill-available', // Fallback for iOS Safari
      backgroundColor: '#000',
      overflow: 'hidden',
      touchAction: 'none',
      userSelect: 'none',
      WebkitUserSelect: 'none'
    }}>
      {/* Video Stack Container */}
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: '100%',
          overflow: 'hidden'
        }}
      >
        {/* Render video stack - current, next, and previous */}
        {episodes.map((episode, index) => {
          const isCurrent = index === currentEpisodeIndex;
          const isNext = index === currentEpisodeIndex + 1;
          const isPrev = index === currentEpisodeIndex - 1;

          // Only render current, next, and previous videos
          if (!isCurrent && !isNext && !isPrev) return null;

          // Only show current video
          if (!isCurrent) return null;

          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 2
              }}
            >
              <video
                ref={getVideoRef(index)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
                src={episode.videoUrl}
                loop={currentEpisodeIndex === totalEpisodes - 1} // Only loop the last episode
                playsInline
                muted={isMuted || !isCurrent}
                poster={drama.coverImage}
              />

              {/* Overlay content - only show for current video */}
              {isCurrent && (
                <>

                  {/* Top Controls */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    padding: '16px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    zIndex: 50
                  }}>
                    <button
                      onClick={toggleMute}
                      style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.4)'
                      }}
                      aria-label={isMuted ? "Unmute" : "Mute"}
                    >
                      {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                    </button>
                  </div>

                  {/* Play/Pause Overlay */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 30,
                      cursor: 'pointer',
                      pointerEvents: isMuted ? 'none' : 'auto'
                    }}
                    onClick={handlePlayPause}
                  >
                    {(() => {
                      const currentRef = getVideoRef(currentEpisodeIndex);
                      const isPaused = currentRef.current?.paused;
                      if (isPaused && !isMuted) {
                        return (
                          <div style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                            backdropFilter: 'blur(15px)',
                            borderRadius: '50%',
                            padding: '20px',
                            animation: 'pulse 2s infinite',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                          }}>
                            <Play size={40} color="white" />
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  {/* Content Overlay - Bottom */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '20px',
                    zIndex: 50,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    gap: '20px'
                  }}>
                    {/* Drama Info - Left Side */}
                    <div style={{
                      flex: 1,
                      minWidth: 0,
                      maxWidth: 'calc(100% - 100px)'
                    }}>
                      <h1 style={{
                        color: 'white',
                        fontSize: '24px',
                        fontWeight: '700',
                        marginBottom: '8px',
                        textShadow: '0 2px 12px rgba(0, 0, 0, 0.9)',
                        lineHeight: '1.2'
                      }}>
                        {drama.title}
                        {totalEpisodes > 1 && (
                          <span style={{
                            fontSize: '16px',
                            fontWeight: '500',
                            marginLeft: '8px',
                            opacity: 0.9
                          }}>
                            {currentEpisodeIndex + 1}/{totalEpisodes}
                          </span>
                        )}
                      </h1>
                      {episode.title && (
                        <p style={{
                          color: 'rgba(255, 255, 255, 0.85)',
                          fontSize: '13px',
                          marginBottom: '8px',
                          textShadow: '0 1px 6px rgba(0, 0, 0, 0.8)',
                          fontWeight: '500'
                        }}>
                          {episode.title}
                        </p>
                      )}
                      <p style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '14px',
                        marginBottom: '12px',
                        textShadow: '0 1px 6px rgba(0, 0, 0, 0.8)',
                        lineHeight: '1.4',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {drama.description}
                      </p>

                      {/* Like Count Display */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        <Heart
                          size={18}
                          fill={currentEpisodeLiked ? '#ef4444' : 'none'}
                          color={currentEpisodeLiked ? '#ef4444' : 'rgba(255, 255, 255, 0.9)'}
                        />
                        <span style={{ textShadow: '0 1px 4px rgba(0, 0, 0, 0.8)' }}>
                          {currentEpisodeLikeCount.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* Episode Navigation & Like Buttons - Right Side */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      flexShrink: 0,
                      marginRight: '-8px' // Move closer to right edge
                    }}>
                      {/* Episode Navigation Buttons */}
                      {totalEpisodes > 1 && (
                        <>
                          {/* Previous Episode Button */}
                          {currentEpisodeIndex > 0 && (
                            <button
                              onClick={() => switchEpisode('prev')}
                              disabled={isTransitioning}
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                border: 'none',
                                cursor: isTransitioning ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                color: 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(8px)',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                transition: 'all 0.2s',
                                opacity: isTransitioning ? 0.3 : 0.7
                              }}
                              onMouseEnter={(e) => {
                                if (!isTransitioning) {
                                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
                                  e.target.style.color = 'rgba(255, 255, 255, 1)';
                                  e.target.style.opacity = '1';
                                  e.target.style.transform = 'scale(1.1)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                                e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                                e.target.style.opacity = '0.7';
                                e.target.style.transform = 'scale(1)';
                              }}
                              aria-label={t('previousEpisode')}
                            >
                              <ChevronUp size={20} />
                            </button>
                          )}

                          {/* Next Episode Button */}
                          {currentEpisodeIndex < totalEpisodes - 1 && (
                            <button
                              onClick={() => switchEpisode('next')}
                              disabled={isTransitioning}
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                border: 'none',
                                cursor: isTransitioning ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                color: 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(8px)',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                transition: 'all 0.2s',
                                opacity: isTransitioning ? 0.3 : 0.7
                              }}
                              onMouseEnter={(e) => {
                                if (!isTransitioning) {
                                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
                                  e.target.style.color = 'rgba(255, 255, 255, 1)';
                                  e.target.style.opacity = '1';
                                  e.target.style.transform = 'scale(1.1)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                                e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                                e.target.style.opacity = '0.7';
                                e.target.style.transform = 'scale(1)';
                              }}
                              aria-label={t('nextEpisode')}
                            >
                              <ChevronDown size={20} />
                            </button>
                          )}
                        </>
                      )}

                      {/* Like Button */}
                      <button
                        onClick={handleLike}
                        style={{
                          width: '70px',
                          height: '70px',
                          borderRadius: '50%',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: currentEpisodeLiked ? '#ef4444' : 'rgba(255, 255, 255, 0.95)',
                          color: currentEpisodeLiked ? 'white' : '#ef4444',
                          boxShadow: currentEpisodeLiked
                            ? '0 8px 24px rgba(239, 68, 68, 0.5)'
                            : '0 8px 24px rgba(0, 0, 0, 0.4)',
                          transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          animation: currentEpisodeLiked ? 'heartBeat 0.6s ease-in-out' : 'none',
                          transform: currentEpisodeLiked ? 'scale(1.1)' : 'scale(1)'
                        }}
                        aria-label="Like"
                      >
                        <Heart
                          size={36}
                          fill={currentEpisodeLiked ? 'currentColor' : 'none'}
                          color="currentColor"
                          strokeWidth={currentEpisodeLiked ? 0 : 2.5}
                        />
                      </button>
                      {/* Like hint text */}
                      {!currentEpisodeLiked && (
                        <div style={{
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '12px',
                          fontWeight: '500',
                          textShadow: '0 1px 4px rgba(0, 0, 0, 0.8)',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          animation: 'pulse 2s infinite',
                          padding: '4px 8px',
                          backgroundColor: 'rgba(0, 0, 0, 0.4)',
                          borderRadius: '12px',
                          backdropFilter: 'blur(8px)'
                        }}>
                          {t('likeHint')}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Unmute Hint - Full screen clickable area */}
                  {isMuted && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 60,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={toggleMute}
                    >
                      <div style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                        backdropFilter: 'blur(12px)',
                        color: 'white',
                        padding: '12px 24px',
                        borderRadius: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        animation: 'pulse 2s infinite',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        userSelect: 'none',
                        pointerEvents: 'auto'
                      }}>
                        <VolumeX size={18} />
                        <span>{t('tapToUnmute')}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ShowTest;
